
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import ReferralTable from '@/components/ReferralTable';
import ReferralForm from '@/components/ReferralForm';
import ChartComponent from '@/components/ChartComponent';
import Achievements from '@/components/Achievements';
import MyProfile from '@/components/MyProfile';
import { ReferralData, getReferralsByCode, getUserStats, checkOnlineStatus } from '@/services/firebase';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Dashboard = () => {
  const [referrals, setReferrals] = useState<ReferralData[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [referrerCode, setReferrerCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log("Dashboard: Browser is online");
      setIsOnline(true);
      // Auto-retry when coming back online
      setTimeout(() => setRetryCount(prev => prev + 1), 1000);
    };
    
    const handleOffline = () => {
      console.log("Dashboard: Browser is offline");
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check online status first
      if (!navigator.onLine) {
        console.warn("Browser reports offline status when fetching referrals");
        setError("You appear to be offline. Please check your internet connection and try again.");
        setLoading(false);
        return;
      }
      
      const code = localStorage.getItem('referrerCode');
      if (!code) {
        setError("No referrer code found. Please log in again.");
        setLoading(false);
        return;
      }
      
      setReferrerCode(code);
      console.log("Fetching referrals for code:", code);
      
      const { referrals: fetchedReferrals, lastVisible: lastDoc } = await getReferralsByCode(code, null);
      
      setReferrals(fetchedReferrals);
      setLastVisible(lastDoc);
      setHasMore(fetchedReferrals.length === 10); // Assuming pageSize is 10
      setCurrentPage(1);
      
      // Get user stats
      try {
        const userStats = await getUserStats(code);
        setStats(userStats);
      } catch (statsError) {
        console.error("Error fetching stats:", statsError);
        // Don't fail the whole operation if just stats fail
        toast.error("Failed to load statistics");
      }
      
    } catch (error) {
      console.error("Error fetching referrals:", error);
      
      // Check if this is a connection error
      if (error.message && (
          error.message.includes("offline") || 
          error.message.includes("network") || 
          error.message.includes("connect")
        )) {
        setError("Unable to connect to the server. Please check your internet connection and try again.");
      } else {
        setError("Failed to load your referrals. Please try again.");
      }
      
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [retryCount]); // Add retryCount to deps so we can trigger a retry
  
  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);
  
  const handleRetry = () => {
    if (!navigator.onLine) {
      toast.error("You are offline. Please check your internet connection first.");
      return;
    }
    
    setRetryCount(prev => prev + 1);
    toast.info("Retrying to load data...");
  };
  
  const handleNextPage = async () => {
    if (!lastVisible || !referrerCode) return;
    
    try {
      setLoading(true);
      
      const { referrals: fetchedReferrals, lastVisible: lastDoc } = await getReferralsByCode(
        referrerCode,
        lastVisible
      );
      
      setReferrals(fetchedReferrals);
      setLastVisible(lastDoc);
      setHasMore(fetchedReferrals.length === 10);
      setCurrentPage(prev => prev + 1);
    } catch (error) {
      console.error("Error fetching next page:", error);
      toast.error("Failed to load more referrals");
    } finally {
      setLoading(false);
    }
  };
  
  const handlePreviousPage = async () => {
    if (currentPage === 1 || !referrerCode) return;
    
    try {
      setLoading(true);
      
      // This is a simplified approach - in a real app, you'd store each page's lastVisible
      // or implement a more complex pagination strategy
      const { referrals: fetchedReferrals, lastVisible: lastDoc } = await getReferralsByCode(
        referrerCode,
        null
      );
      
      setReferrals(fetchedReferrals);
      setLastVisible(lastDoc);
      setHasMore(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching previous page:", error);
      toast.error("Failed to load previous referrals");
    } finally {
      setLoading(false);
    }
  };
  
  // Show online/offline status indicator
  const NetworkStatus = () => (
    <div className={`flex items-center justify-center space-x-2 mb-2 ${isOnline ? 'text-green-500' : 'text-red-500'}`}>
      {isOnline ? (
        <>
          <Wifi className="h-4 w-4" />
          <span className="text-sm font-medium">Online</span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Offline</span>
        </>
      )}
    </div>
  );
  
  return (
    <ProtectedRoute>
      <Layout>
        <div className="space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">Your Dashboard</h1>
              <p className="text-muted-foreground">
                Manage and track all of your referrals
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <MyProfile />
              <ReferralForm onSuccess={fetchReferrals} />
            </div>
          </div>
          
          {!isOnline && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
              <WifiOff className="h-5 w-5 mr-2 text-red-500" />
              <p className="text-red-700">
                You are currently offline. Some features may be unavailable until you reconnect.
              </p>
            </div>
          )}
          
          {stats && (
            <ChartComponent data={stats} />
          )}
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Your Referrals</h2>
              
              {isOnline && (
                <Button 
                  onClick={fetchReferrals} 
                  variant="outline"
                  size="sm"
                  className="flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              )}
            </div>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-pulse text-xl">Loading referrals...</div>
              </div>
            ) : error ? (
              <div className="text-center py-8 space-y-4">
                <div className="flex items-center justify-center space-x-2 text-amber-500">
                  <AlertTriangle className="h-6 w-6" />
                  <p className="text-lg font-medium">{error}</p>
                </div>
                
                <NetworkStatus />
                
                <Button 
                  onClick={handleRetry} 
                  className="flex items-center space-x-2"
                  disabled={!isOnline}
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Try Again</span>
                </Button>
              </div>
            ) : (
              <ReferralTable 
                referrals={referrals}
                pagination={{
                  currentPage,
                  hasMore,
                  onNext: handleNextPage,
                  onPrevious: handlePreviousPage
                }}
              />
            )}
          </div>
          
          {stats && stats.achievements && (
            <Achievements 
              achievements={stats.achievements}
              totalReferrals={stats.totalReferrals}
              soldReferrals={stats.soldReferrals}
            />
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  );
};

export default Dashboard;
